<script>
  import TargetRecorder from './TargetRecorder.svelte'
  import { listSamples, liveSamples, putSample, safeName, extFor, recorderIssue } from './recordings.js'
  import { uploadSample, uploadConfigured } from './upload.js'

  // Record the whole word and each of its phonemes. Recorded sounds are used
  // automatically; each can be switched back to the built-in "Standard" sound.
  let { word, onclose } = $props()

  const micIssue = recorderIssue()
  const spelling = $derived(word.sounds.map((s) => s.text).join(''))
  const blendKey = (codes) => 'bl:' + codes.join('_')

  // Targets: the whole word + any saved blends (e.g. "boo") + each DISTINCT phoneme.
  // Derived so the panel re-targets if the word prop changes while it's open.
  const targets = $derived.by(() => {
    const sp = word.sounds.map((s) => s.text).join('')
    const ss = word.sounds
    let blinks = new Set()
    try { const raw = localStorage.getItem('blend:' + sp); if (raw) blinks = new Set(JSON.parse(raw)) } catch (_) {}
    // Blends configured for this word (from the Blend tool) — record the joined sound.
    // Letters inside a blend are covered by it, so they don't get their own row.
    const blendTargets = []; const covered = new Set(); let i = 0
    while (i < ss.length) {
      let j = i
      while (j < ss.length - 1 && blinks.has(j)) j++
      if (j > i) {
        for (let k = i; k <= j; k++) covered.add(k)
        const codes = ss.slice(i, j + 1).map((x) => x.code)
        blendTargets.push({ key: blendKey(codes), kind: 'blend', label: ss.slice(i, j + 1).map((x) => x.text).join(''), sub: `/${codes.join('')}/` })
      }
      i = j + 1
    }
    const seen = new Set(); const phonemeTargets = []
    for (let k = 0; k < ss.length; k++) {
      if (covered.has(k)) continue // part of a blend → no separate row
      const s = ss[k]; const key = `ph:${s.code}`
      if (seen.has(key)) continue
      seen.add(key)
      phonemeTargets.push({ key, kind: 'phoneme', label: s.text, sub: `/${s.code}/` })
    }
    return [
      { key: word.wordAudioKey, kind: 'word', label: `Whole word: ${sp}`, sub: '', phonicKey: 'phonicword:' + word.wordAudioKey },
      ...blendTargets,
      ...phonemeTargets,
    ]
  })

  let reloadKey = $state(0)
  let status = $state('')
  let busy = $state(false)

  async function saveAll() {
    if (!uploadConfigured()) { status = '📱 Saved on this device (no server set).'; return }
    busy = true; status = 'Saving…'
    let ok = 0, fail = 0
    for (const t of targets) {
      const samples = liveSamples(await listSamples(t.key))
      for (const s of samples) {
        if (s.uploaded) continue
        try { await uploadSample(safeName(t.key), s.slot, s.blob, extFor(s.mime)); await putSample({ ...s, uploaded: true }); ok++ }
        catch (_) { fail++ }
      }
    }
    reloadKey++ // refresh the cards so the "saved" dots update
    status = fail ? `Saved ${ok}, ${fail} failed.` : (ok ? `Saved ${ok} to the server ✓` : 'Nothing new to save.')
    busy = false
  }
</script>

<svelte:window onkeydown={(e) => { if (e.key === 'Escape') onclose() }} />

<!-- svelte-ignore a11y_click_events_have_key_events a11y_interactive_supports_focus -->
<div class="backdrop" onclick={(e) => { if (e.currentTarget === e.target) onclose() }} role="presentation">
  <div class="panel" role="dialog" aria-modal="true" aria-label="Record sounds and word">
    <button class="x" onclick={onclose} aria-label="Close">✕</button>
    <h2>🎙 Record “{spelling}”</h2>
    <p class="tip">Recorded sounds are used automatically. Switch any to <b>Standard</b> to use the built-in sound.</p>
    {#if micIssue}<p class="mic-warn">⚠ {micIssue}</p>{/if}

    <div class="list">
      {#each targets as t (t.key)}
        <TargetRecorder target={t} {reloadKey} phonicKey={t.phonicKey || null} />
      {/each}
    </div>

    <button class="save" onclick={saveAll} disabled={busy}>💾 Save to server</button>
    {#if status}<p class="status">{status}</p>{/if}
  </div>
</div>

<style>
  .backdrop {
    position: fixed; inset: 0; z-index: 50; background: rgba(0, 0, 0, 0.5);
    display: flex; align-items: center; justify-content: center; padding: 2vh;
  }
  .panel {
    position: relative; width: min(94vw, 520px); max-height: 92vh; overflow: hidden;
    background: #fffef0; border-radius: 20px; padding: 3vh 2.6vh 2.4vh;
    box-shadow: 0 20px 50px rgba(0, 0, 0, 0.4);
    display: flex; flex-direction: column; gap: 1.4vh;
  }
  .x {
    position: absolute; top: 1.2vh; right: 1.2vh; border: none; background: transparent;
    font-size: 2.4vh; color: #2e5d1f; cursor: pointer; line-height: 1;
  }
  h2 { margin: 0 2vh 0 0; font-size: 2.6vh; color: #2e5d1f; text-align: center; }
  .tip { margin: 0; text-align: center; color: #4a7a34; font-size: 1.6vh; font-weight: 600; }
  .mic-warn { margin: 0; text-align: center; color: #a33; background: #ffe9e6; border-radius: 10px; padding: 0.9vh 1.2vh; font-size: 1.6vh; font-weight: 700; }
  .list { display: flex; flex-direction: column; gap: 1vh; overflow-y: auto; padding: 0.2vh; }
  .save {
    font-family: inherit; font-weight: 800; font-size: 2.1vh; border: none; border-radius: 999px;
    padding: 1.3vh; cursor: pointer; background: #2e5d1f; color: #fffef0; box-shadow: 0 4px 0 rgba(0, 0, 0, 0.18);
  }
  .save:disabled { opacity: 0.5; cursor: default; }
  .save:active { transform: translateY(2px); box-shadow: 0 2px 0 rgba(0, 0, 0, 0.18); }
  .status { margin: 0; text-align: center; color: #2e5d1f; font-weight: 700; font-size: 1.7vh; }
</style>
